import { Signal, computed, signal } from '@angular/core'
import { isEqual as areDeeplyEqual } from 'lodash-es'

export interface SubmittedFilterSnapshot<TViewFilters, TRequestFilters> {
  view: TViewFilters
  request: TRequestFilters
}

export interface SubmittedFilterAdapters<TViewFilters> {
  saveView(viewFilters: TViewFilters): void
  refetch(): unknown
}

export interface SubmittedFilterSubmitOptions {
  refetchOnUnchanged?: boolean
}

export interface SubmittedFilterState<TViewFilters, TRequestFilters> {
  readonly snapshot: Signal<SubmittedFilterSnapshot<TViewFilters, TRequestFilters> | undefined>
  readonly ready: Signal<boolean>
  commit(viewFilters: TViewFilters, requestFilters: TRequestFilters): boolean
  submit(viewFilters: TViewFilters, requestFilters: TRequestFilters, options?: SubmittedFilterSubmitOptions): void
}

/**
 * Keeps submit-only form state separate from the immutable filter snapshot used
 * by TanStack Query. `submit` owns the shared commit, persistence, and explicit
 * unchanged-request refetch protocol. `commit` remains available for initial
 * setup, where callers control the first fetch separately.
 */
export function createSubmittedFilterState<TViewFilters, TRequestFilters>(
  adapters: SubmittedFilterAdapters<TViewFilters>
): SubmittedFilterState<TViewFilters, TRequestFilters> {
  const submitted = signal<SubmittedFilterSnapshot<TViewFilters, TRequestFilters> | undefined>(undefined)

  const commitSnapshot = (viewFilters: TViewFilters, requestFilters: TRequestFilters) => {
    const next = {
      view: structuredClone(viewFilters),
      request: structuredClone(requestFilters),
    }
    const requestChanged = !submitted() || !areDeeplyEqual(submitted()?.request, next.request)
    submitted.set(next)

    return { snapshot: next, requestChanged }
  }

  return {
    snapshot: submitted.asReadonly(),
    ready: computed(() => submitted() !== undefined),
    commit: (viewFilters, requestFilters) => commitSnapshot(viewFilters, requestFilters).requestChanged,
    submit: (viewFilters, requestFilters, options) => {
      const { snapshot, requestChanged } = commitSnapshot(viewFilters, requestFilters)
      adapters.saveView(snapshot.view)
      if (!requestChanged && options?.refetchOnUnchanged !== false) adapters.refetch()
    },
  }
}
